import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";

const I18nContext = createContext(null);

export const DEFAULT_LANG = "en";
export const SUPPORTED_LANGS = [
    { code: "en", name: "English", native: "English" },
    { code: "hi", name: "Hindi", native: "हिन्दी" },
    { code: "hinglish", name: "Hinglish", native: "Hinglish" },
    { code: "mr", name: "Marathi", native: "मराठी" },
    { code: "ta", name: "Tamil", native: "தமிழ்" },
    { code: "te", name: "Telugu", native: "తెలుగు" },
    { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
    { code: "bn", name: "Bengali", native: "বাংলা" },
];

const LS_LANG = "vyapaar_lang";
const LS_DICT = "vyapaar_dict_v1";

// Fallback English dict loaded via API; keep an in-memory copy to avoid flicker.
const dictCache = new Map();

async function fetchDict(lang) {
    if (dictCache.has(lang)) return dictCache.get(lang);
    // Try localStorage snapshot
    try {
        const snap = JSON.parse(localStorage.getItem(`${LS_DICT}:${lang}`) || "null");
        if (snap && snap.en && snap.lang) {
            dictCache.set(lang, snap);
            // refresh in background
            api.get(`/i18n/${lang}`).then((r) => {
                dictCache.set(lang, r.data);
                localStorage.setItem(`${LS_DICT}:${lang}`, JSON.stringify(r.data));
            }).catch(() => {});
            return snap;
        }
    } catch {}
    const { data } = await api.get(`/i18n/${lang}`);
    dictCache.set(lang, data);
    try { localStorage.setItem(`${LS_DICT}:${lang}`, JSON.stringify(data)); } catch {}
    return data;
}

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(() => localStorage.getItem(LS_LANG) || DEFAULT_LANG);
    const [dict, setDict] = useState({ en: {}, lang: {} });
    const [templates, setTemplates] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const d = await fetchDict(lang);
                if (!active) return;
                setDict(d);
                setTemplates(d.templates || {});
            } catch (e) {
                // keep whatever we had — never block UI
            } finally {
                if (active) setReady(true);
            }
        })();
        return () => { active = false; };
    }, [lang]);

    const setLang = useCallback(async (newLang) => {
        if (!newLang || newLang === lang) return;
        localStorage.setItem(LS_LANG, newLang);
        setLangState(newLang);
        // Persist to user profile if logged in; silent-fail if guest.
        try { await api.put("/me/language", { language: newLang }); } catch {}
    }, [lang]);

    const t = useCallback((key, fallback) => {
        const v = dict?.lang?.[key];
        if (v) return v;
        const e = dict?.en?.[key];
        if (e) return e;
        return fallback ?? key;
    }, [dict]);

    const value = useMemo(() => ({
        lang, setLang, t, ready, dict, templates,
        languages: SUPPORTED_LANGS,
    }), [lang, setLang, t, ready, dict, templates]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be used within I18nProvider");
    return ctx;
}

// Hook helper to pick user language from backend "me" response once logged in.
export function syncUserLanguage(user, setLang) {
    if (user?.language && user.language !== localStorage.getItem(LS_LANG)) {
        localStorage.setItem(LS_LANG, user.language);
        setLang(user.language);
    }
}
